import React, { type ReactNode } from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import { CONTENT_TYPE } from "~constants";
import LiveblogDecoration from "~components/shared/liveblog-decoration/LiveblogDecoration";
import { HEADLINE_SIZE } from "~types/lineup";
import { formatHeadline } from "~utils/formatHeadline";

import styles from "./Headline.module.css";

interface HeadlineProps {
    className?: string;
    contentType?: string;
    forceLinking?: boolean;
    isFeaturedStyle?: boolean;
    isPhoenix?: boolean;
    preamble?: ReactNode;
    size?: string;
    storyIndex?: number;
    text: string;
    url?: string;
}

interface HeadlineContentProps {
    children: ReactNode;
    forceLinking?: boolean;
    isPhoenix?: boolean;
    relatedIndex?: number;
    storyIndex?: number;
    url?: string;
}

function HeadlineContent({
    children,
    forceLinking,
    isPhoenix,
    relatedIndex,
    storyIndex,
    url,
}: HeadlineContentProps) {
    const optionalDataAttributes = {} as Record<string, string | number>;

    if (relatedIndex !== undefined) {
        optionalDataAttributes["data-link-related-type"] = "Related";
        optionalDataAttributes["data-link-related-index"] = relatedIndex;
    }

    if (url && (!isPhoenix || forceLinking)) {
        return (
            <a
                {...optionalDataAttributes}
                data-link-index={storyIndex}
                href={url}
            >
                {children}
            </a>
        );
    }
    return <span>{children}</span>;
}

function Headline({
    className,
    contentType,
    forceLinking,
    isFeaturedStyle,
    isPhoenix,
    preamble,
    size = HEADLINE_SIZE.LARGE,
    storyIndex,
    text,
    url,
}: HeadlineProps) {
    return (
        <div
            className={clsx(
                styles.phoenix,
                {
                    [styles.feature]: isFeaturedStyle,
                    [styles.interactive]: forceLinking,
                    [typographyStyles.articleHeadlineXxl]:
                        size === HEADLINE_SIZE.EXTRA_EXTRA_LARGE,
                    [`${typographyStyles.articleHeadlineXl} ${styles.extraLarge}`]:
                        size === HEADLINE_SIZE.EXTRA_LARGE,
                    [`${typographyStyles.articleHeadlineLg} ${styles.large}`]:
                        size === HEADLINE_SIZE.LARGE,
                    [typographyStyles.articleHeadlineMd]:
                        size === HEADLINE_SIZE.MEDIUM ||
                        size === HEADLINE_SIZE.FULL_ZONE,
                    [typographyStyles.articleHeadlineSm]:
                        size === HEADLINE_SIZE.SMALL,
                    [typographyStyles.articleHeadlineXs]:
                        size === HEADLINE_SIZE.EXTRA_SMALL,
                },
                className
            )}
            data-component="headline"
            data-testid="headline"
        >
            <HeadlineContent
                url={url}
                storyIndex={storyIndex}
                isPhoenix={isPhoenix}
                forceLinking={forceLinking}
            >
                {contentType === CONTENT_TYPE.liveblog &&
                    !isPhoenix &&
                    !preamble && <LiveblogDecoration />}
                {preamble}
                {formatHeadline(text)}
            </HeadlineContent>
        </div>
    );
}

export default Headline;
